import React from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { Tenant } from './tenantGroups';

export interface TenantDisplayProps {
  isMasked: (tenantId: string) => boolean;
  maskName: (name: string, tenantId: string) => string;
  onToggleMask: (tenantId: string) => void;
  onCopyId: (tenantId: string) => void;
  onView: (tenant: Tenant) => void;
}

export const getStatusColor = (status: string) => {
  switch (status) {
    case 'Provisioned':
      return 'bg-zinc-800 text-gray-300 border-zinc-600';
    case 'InProgress':
      return 'bg-zinc-800 border-red-700/50';
    case 'Failed':
      return 'bg-red-900/30 border-red-700';
    default:
      return 'bg-zinc-800 text-gray-300 border-zinc-600';
  }
};

export const compliancePercent = (compliant: number, total: number) => (total > 0 ? ((compliant / total) * 100).toFixed(2) + '%' : 'N/A');

const formatDate = (value: string, options: Intl.DateTimeFormatOptions) => {
  const date = new Date(value);
  return value && !Number.isNaN(date.getTime()) ? date.toLocaleDateString('en-US', options) : '';
};

export interface RowStat {
  label: string;
  value: React.ReactNode;
}

interface TenantRowLayoutProps extends Pick<TenantDisplayProps, 'isMasked' | 'maskName' | 'onToggleMask' | 'onCopyId'> {
  tenant: Tenant;
  tag: string;
  // Third line under the name and id (tenant count for a reseller, onboarding date for a tenant).
  summary: React.ReactNode;
  stats: RowStat[];
  actionLabel: string;
  onAction: () => void;
  // When set, the name toggles the row open/closed (reseller boxes).
  toggle?: { expanded: boolean; onToggle: () => void; controls: string };
}

/**
 * One tenant or reseller as a row: name, then id with status, then a summary line on the left; stats in the
 * middle; the view button on the right. Wraps onto several lines on narrow screens.
 */
export const TenantRowLayout = ({
  tenant,
  tag,
  summary,
  stats,
  actionLabel,
  onAction,
  toggle,
  isMasked,
  maskName,
  onToggleMask,
  onCopyId,
}: TenantRowLayoutProps) => {
  const masked = isMasked(tenant.tenantId);
  // Lines under the name start under the name, not under the chevron.
  const indent = toggle ? 'pl-8' : '';
  const name = (
    <span className="flex min-w-0 items-center gap-2">
      <span className="truncate text-lg font-bold text-white">{maskName(tenant.tenantName, tenant.tenantId)}</span>
      <span className="flex-shrink-0 rounded-full border border-zinc-600 bg-zinc-800 px-2.5 py-0.5 text-xs font-semibold text-gray-300">
        {tag}
      </span>
    </span>
  );

  return (
    <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
      <div className="min-w-[260px] flex-1">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {toggle ? (
              // Chevron + name are one button: clicking either opens or closes the reseller.
              <button
                type="button"
                onClick={toggle.onToggle}
                aria-expanded={toggle.expanded}
                aria-controls={toggle.controls}
                title={toggle.expanded ? 'Hide tenants' : 'Show tenants'}
                className="flex min-w-0 items-center gap-2 text-left"
              >
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-zinc-800 text-gray-300">
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    aria-hidden="true"
                    className={`transition-transform ${toggle.expanded ? 'rotate-90' : ''}`}
                  >
                    <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                {name}
              </button>
            ) : (
              name
            )}
            <button
              type="button"
              className="flex-shrink-0 text-gray-400 transition-colors hover:text-white"
              title={masked ? 'Show tenant name' : 'Hide tenant name'}
              aria-label={masked ? 'Show tenant name' : 'Hide tenant name'}
              onClick={() => onToggleMask(tenant.tenantId)}
            >
              {masked ? <FaEye size={14} /> : <FaEyeSlash size={14} />}
            </button>
          </div>

          <div className={`mt-1.5 flex flex-wrap items-center gap-2 ${indent}`}>
            <button
              type="button"
              className="font-mono text-xs text-gray-500 transition-colors hover:text-red-400"
              onClick={() => onCopyId(tenant.tenantId)}
              title="Click to copy ID"
            >
              {tenant.tenantId.substring(0, 8)}...
            </button>
            <span
              className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${getStatusColor(tenant.status)}`}
            >
              {tenant.status}
            </span>
            <span
              className={`h-2.5 w-2.5 rounded-full ${tenant.isActive ? 'bg-green-500' : 'bg-red-600'}`}
              title={tenant.isActive ? 'Active' : 'Inactive'}
              aria-label={tenant.isActive ? 'Active' : 'Inactive'}
              role="img"
            ></span>
          </div>

          <div className={`mt-1.5 text-xs text-gray-400 ${indent}`}>{summary}</div>
        </div>
      </div>

      <dl className="m-0 flex flex-wrap items-center gap-x-8 gap-y-3">
        {stats.map((stat) => (
          <div key={stat.label} className="min-w-[72px]">
            <dt className="text-[11px] uppercase tracking-wide text-gray-500">{stat.label}</dt>
            <dd className="m-0 text-base font-bold text-white">{stat.value}</dd>
          </div>
        ))}
      </dl>

      <button
        type="button"
        onClick={onAction}
        className="flex flex-shrink-0 items-center gap-2 rounded-lg bg-zinc-700 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-red-600"
      >
        <FaEye size={14} />
        {actionLabel}
      </button>
    </div>
  );
};

// One tenant as a row on the Tenant Management page.
const TenantRow = ({ tenant, onView, ...display }: TenantDisplayProps & { tenant: Tenant }) => (
  <article
    aria-label={`Tenant ${display.maskName(tenant.tenantName, tenant.tenantId)}`}
    className="rounded-xl border border-zinc-700 bg-zinc-800 p-4 transition-colors hover:border-zinc-500"
  >
    <TenantRowLayout
      tenant={tenant}
      tag={tenant.customerType || 'Tenant'}
      summary={`Onboarded ${formatDate(tenant.onboardingDate, { year: 'numeric', month: 'short', day: 'numeric' })}`}
      stats={[
        { label: 'Compliance', value: compliancePercent(tenant.compliantAgentCount, tenant.totalAgentCount) },
        { label: 'Devices', value: (tenant.totalAgentCount ?? 0).toLocaleString() },
        { label: 'Licenses', value: (tenant.totalLicenses ?? 0).toLocaleString() },
        { label: 'In use', value: (tenant.totalAgentCount ?? 0).toLocaleString() },
        {
          label: 'Expiry',
          value: formatDate(tenant.licensesActivateToDate, { day: '2-digit', month: 'short', year: 'numeric' }) || '—',
        },
      ]}
      actionLabel="View Details"
      onAction={() => onView(tenant)}
      {...display}
    />
  </article>
);

export default TenantRow;
